'use client';

import { type MouseEvent, useCallback, useMemo, useState } from 'react';

import type {
  TableOnSortChangeStateType,
  TableRootPropsInterface,
} from '../types/table-root.types';

/**
 * A hook that implements controlled and uncontrolled sorting behavior.
 *
 * Manages the sorting state and transitions between sort directions for table columns.
 * Supports both controlled mode (when sortBy/sortDirection props are provided) and
 * uncontrolled mode (using internal state with defaultSort props).
 */
export const useSortableTable = (props: TableRootPropsInterface) => {
  const {
    defaultSortBy,
    defaultSortDirection,
    sortBy: controlledSortBy,
    sortDirection: controlledSortDirection,
    onSortChange: setControlledSortBy,
    sortDirectionOrder = ['ascending', 'descending'],
    ...rest
  } = props;

  // Internal state for uncontrolled mode
  const [uncontrolledSortBy, setUncontrolledSortBy] = useState(defaultSortBy);
  const [uncontrolledSortDirection, setUncontrolledSortDirection] = useState(defaultSortDirection);

  // Check if in controlled mode (sortBy and sortDirection exist in props)
  const isControlled = 'sortBy' in props && 'sortDirection' in props;

  // Combine controlled and uncontrolled values into current sort state
  const currentSortState = useMemo(
    () => ({
      sortBy: controlledSortBy ?? uncontrolledSortBy,
      sortDirection: controlledSortDirection ?? uncontrolledSortDirection,
    }),
    [controlledSortBy, controlledSortDirection, uncontrolledSortBy, uncontrolledSortDirection],
  );

  // Handle sort state changes for both controlled and uncontrolled modes
  const onSortChange = useCallback(
    (state: TableOnSortChangeStateType, event: MouseEvent<HTMLTableCellElement>) => {
      if (isControlled) {
        // Controlled mode: just call the provided handler
        setControlledSortBy?.(state, event);
      } else {
        // Uncontrolled mode: update internal state
        setUncontrolledSortBy(state.sortBy);
        setUncontrolledSortDirection(state.sortDirection);

        // Call change handler if provided
        setControlledSortBy?.(state, event);
      }
    },
    [isControlled, setControlledSortBy],
  );

  // Combine all sorting-related props into a memoized context object
  const context = useMemo(
    () => ({
      onSortChange,
      sortBy: currentSortState.sortBy,
      sortDirection: currentSortState.sortDirection,
      sortDirectionOrder,
    }),
    [onSortChange, currentSortState, sortDirectionOrder],
  );

  return useMemo(() => ({ context, rest }), [context, rest]);
};
